import { ApplicationPermissions, HasuraRoles, PermissionsInheritToChildGroups, PermissionsInheritToParentGroups, } from "common";
import React, { createContext, useCallback, useContext, useEffect, useMemo, } from "react";
import { useUserByIdForPermissionsQuery, useUserGroupChildIdsByGroupIdLazyQuery, useUserGroupParentIdsByGroupIdLazyQuery, } from "../../graphql/__generated__";
import useHasuraRole from "../../hooks/useHasuraRole";
import { getPermissionsForGroup } from "./utils";
export const PermissionsContext = createContext({
    hasPermission: () => false,
    hasPermissionInAnyGroups: () => false,
});
export const usePermissions = () => useContext(PermissionsContext);
export default function PermissionsProvider({ userId, children, debug, }) {
    var _a;
    const { loading: userLoading, data: userData, error: userError, } = useUserByIdForPermissionsQuery({
        variables: {
            userId,
        },
    });
    const { loading: hasuraRoleLoading, data: hasuraRole, error: hasuraRoleError, } = useHasuraRole();
    const userGroupIdToQueryChildIds = useMemo(() => {
        var _a;
        if (userLoading || hasuraRoleLoading || !hasuraRole)
            return [];
        const userGroupIdsSet = new Set();
        const adminAccessUserGroupId = new Set();
        (_a = userData === null || userData === void 0 ? void 0 : userData.users_by_pk) === null || _a === void 0 ? void 0 : _a.flattened_user_group_user_permissions.filter(({ user_group_id, permission_id }) => {
            if (permission_id === ApplicationPermissions.ADMIN_ACCESS) {
                adminAccessUserGroupId.add(user_group_id);
            }
            return PermissionsInheritToChildGroups[hasuraRole].includes(permission_id);
        }).forEach(({ user_group_id }) => {
            if (hasuraRole === HasuraRoles.ADMIN_USER ||
                hasuraRole === HasuraRoles.SPACECLOUD_ADMIN) {
                if (adminAccessUserGroupId.has(user_group_id)) {
                    userGroupIdsSet.add(user_group_id);
                }
            }
            else {
                userGroupIdsSet.add(user_group_id);
            }
        });
        return [...userGroupIdsSet];
    }, [userLoading, userData, hasuraRole, hasuraRoleLoading]);
    const userGroupIdToQueryParentIds = useMemo(() => {
        var _a;
        if (userLoading || hasuraRoleLoading || !hasuraRole)
            return [];
        const userGroupIdsSet = new Set();
        const adminAccessUserGroupId = new Set();
        (_a = userData === null || userData === void 0 ? void 0 : userData.users_by_pk) === null || _a === void 0 ? void 0 : _a.flattened_user_group_user_permissions.filter(({ user_group_id, permission_id }) => {
            if (permission_id === ApplicationPermissions.ADMIN_ACCESS) {
                adminAccessUserGroupId.add(user_group_id);
            }
            return PermissionsInheritToParentGroups[hasuraRole].includes(permission_id);
        }).forEach(({ user_group_id }) => {
            if (hasuraRole === HasuraRoles.ADMIN_USER ||
                hasuraRole === HasuraRoles.SPACECLOUD_ADMIN) {
                if (adminAccessUserGroupId.has(user_group_id)) {
                    userGroupIdsSet.add(user_group_id);
                }
            }
            else {
                userGroupIdsSet.add(user_group_id);
            }
        });
        return [...userGroupIdsSet];
    }, [userLoading, userData, hasuraRole, hasuraRoleLoading]);
    const [fetchUserGroupChildIdsByGroupId, { data: childIdsData, loading: childIdsLoading, error: childIdsError, called: childIdsCalled, },] = useUserGroupChildIdsByGroupIdLazyQuery();
    const [fetchUserGroupParentIdsByGroupId, { data: parentIdsData, loading: parentIdsLoading, error: parentIdsError, called: parentIdsCalled, },] = useUserGroupParentIdsByGroupIdLazyQuery();
    useEffect(() => {
        if (userGroupIdToQueryChildIds.length > 0) {
            fetchUserGroupChildIdsByGroupId({
                variables: {
                    groupIds: userGroupIdToQueryChildIds,
                },
            });
        }
    }, [userGroupIdToQueryChildIds, fetchUserGroupChildIdsByGroupId]);
    useEffect(() => {
        if (userGroupIdToQueryParentIds.length > 0) {
            fetchUserGroupParentIdsByGroupId({
                variables: {
                    groupIds: userGroupIdToQueryParentIds,
                },
            });
        }
    }, [userGroupIdToQueryParentIds, fetchUserGroupParentIdsByGroupId]);
    const loading = useMemo(() => {
        if (userLoading)
            return true;
        if (hasuraRoleLoading)
            return true;
        const pLoading = (() => {
            if (userGroupIdToQueryParentIds.length > 0) {
                if (parentIdsCalled)
                    return parentIdsLoading;
                return true;
            }
            return false;
        })();
        const cLoading = (() => {
            if (userGroupIdToQueryChildIds.length > 0) {
                if (childIdsCalled)
                    return childIdsLoading;
                return true;
            }
            return false;
        })();
        return cLoading || pLoading;
    }, [
        userLoading,
        userGroupIdToQueryChildIds,
        childIdsLoading,
        childIdsCalled,
        userGroupIdToQueryParentIds,
        parentIdsLoading,
        parentIdsCalled,
        hasuraRoleLoading,
    ]);
    const permissions = useMemo(() => {
        if (userLoading || hasuraRoleLoading || !hasuraRole)
            return {
                group: new Map(),
            };
        const group = (() => {
            var _a, _b;
            if (((_a = userData === null || userData === void 0 ? void 0 : userData.users_by_pk) === null || _a === void 0 ? void 0 : _a.flattened_user_group_user_permissions.length) ===
                0)
                return new Map();
            const groupPermissionsMap = new Map();
            (_b = userData === null || userData === void 0 ? void 0 : userData.users_by_pk) === null || _b === void 0 ? void 0 : _b.flattened_user_group_user_permissions.forEach((groupPermission) => {
                var _a;
                const { user_group_id: groupId, permission_id: permissionName } = groupPermission;
                if (groupPermissionsMap.get(groupId)) {
                    (_a = groupPermissionsMap.get(groupId)) === null || _a === void 0 ? void 0 : _a.add(permissionName);
                }
                else {
                    groupPermissionsMap.set(groupId, new Set([permissionName]));
                }
            });
            // Parent Group Permissions Inherit to Child Group
            const parentIdFromChildIdMap = {};
            const uniqueChildIds = new Set();
            childIdsData === null || childIdsData === void 0 ? void 0 : childIdsData.flattened_user_groups.forEach(({ parent_id: parentId, child_id: childId }) => {
                uniqueChildIds.add(childId);
                if (parentIdFromChildIdMap[childId]) {
                    parentIdFromChildIdMap[childId].push(parentId);
                }
                else {
                    parentIdFromChildIdMap[childId] = [parentId];
                }
            });
            uniqueChildIds.forEach((childId) => {
                const parentIds = parentIdFromChildIdMap[childId];
                const parentIdWithPermissions = parentIds.find((parentId) => groupPermissionsMap.get(parentId) != null);
                if (parentIdWithPermissions != null) {
                    const parentPermissions = groupPermissionsMap.get(parentIdWithPermissions);
                    const sanitizeParentPermissions = PermissionsInheritToChildGroups[hasuraRole].filter((p) => parentPermissions === null || parentPermissions === void 0 ? void 0 : parentPermissions.has(p));
                    const currentPermissions = groupPermissionsMap.get(childId);
                    if (currentPermissions) {
                        sanitizeParentPermissions.forEach((p) => currentPermissions.add(p));
                    }
                    else {
                        groupPermissionsMap.set(childId, new Set(sanitizeParentPermissions));
                    }
                }
            });
            // Child Group Permissions Inherit to Parent Group
            const childIdFromParentIdMap = {};
            const uniqueParentIds = new Set();
            parentIdsData === null || parentIdsData === void 0 ? void 0 : parentIdsData.flattened_user_groups.forEach(({ parent_id: parentId, child_id: childId }) => {
                uniqueParentIds.add(parentId);
                if (childIdFromParentIdMap[parentId]) {
                    childIdFromParentIdMap[parentId].push(childId);
                }
                else {
                    childIdFromParentIdMap[parentId] = [childId];
                }
            });
            uniqueParentIds.forEach((parentId) => {
                const childIds = childIdFromParentIdMap[parentId];
                const childIdWithPermissions = childIds.find((childId) => groupPermissionsMap.get(childId) != null);
                if (childIdWithPermissions != null) {
                    const childPermissions = groupPermissionsMap.get(childIdWithPermissions);
                    const sanitizeChildPermissions = PermissionsInheritToParentGroups[hasuraRole].filter((p) => childPermissions === null || childPermissions === void 0 ? void 0 : childPermissions.has(p));
                    const currentPermissions = groupPermissionsMap.get(parentId);
                    if (currentPermissions) {
                        sanitizeChildPermissions.forEach((p) => currentPermissions.add(p));
                    }
                    else {
                        groupPermissionsMap.set(parentId, new Set(sanitizeChildPermissions));
                    }
                }
            });
            return groupPermissionsMap;
        })();
        return {
            group,
        };
    }, [
        userData,
        userLoading,
        childIdsData,
        parentIdsData,
        hasuraRole,
        hasuraRoleLoading,
    ]);
    if (userError)
        throw userError;
    if (hasuraRoleError)
        throw hasuraRoleError;
    if (childIdsError)
        throw childIdsError;
    if (parentIdsError)
        throw parentIdsError;
    useEffect(() => {
        if (debug && permissions && !loading) {
            // eslint-disable-next-line no-console
            console.groupCollapsed("PermissionsProvider");
            // eslint-disable-next-line no-console
            console.log("permissions", permissions);
            // eslint-disable-next-line no-console
            console.groupEnd();
        }
    }, [permissions, debug, loading]);
    const getHasGroupPermission = useCallback((permission, groupId) => {
        if (!groupId)
            return false;
        const permissionsForGroup = getPermissionsForGroup(groupId, permissions.group);
        const hasGroupPermission = permissionsForGroup.has(permission);
        return hasGroupPermission;
    }, [permissions.group]);
    const hasPermissionInAnyGroups = useCallback((permission, applicationPermission) => {
        for (const [, permissionSet] of permissions.group) {
            if (applicationPermission) {
                if (permissionSet.has(permission) &&
                    permissionSet.has(applicationPermission)) {
                    return true;
                }
            }
            else if (permissionSet.has(permission)) {
                return true;
            }
        }
        return false;
    }, [permissions.group]);
    const hasPermission = useCallback((permission, groupId) => {
        const hasGroupPermission = getHasGroupPermission(permission, groupId);
        return hasGroupPermission;
    }, [getHasGroupPermission]);
    return (React.createElement(PermissionsContext.Provider, { value: {
            ...permissions,
            hasPermission,
            hasPermissionInAnyGroups,
            userId,
            userEmail: ((_a = userData === null || userData === void 0 ? void 0 : userData.users_by_pk) === null || _a === void 0 ? void 0 : _a.email) || "",
            loading,
        } }, children));
}
//# sourceMappingURL=index.js.map